import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setIsValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setIsValid(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setIsComplete(true);
      toast.success('Password reimpostata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B6B93]/80 to-[#4BA3C7]/60" />
        <Card className="w-full max-w-md glass border-white/20 shadow-2xl relative z-10">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#1B6B93]" />
            <p className="mt-4 text-slate-600">Verifica del link in corso...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B6B93]/80 to-[#4BA3C7]/60" />
        <Card className="w-full max-w-md glass border-white/20 shadow-2xl relative z-10">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Link non valido</h2>
            <p className="text-slate-600 mb-6">
              Questo link per il reset della password non è valido o è scaduto.
            </p>
            <Button onClick={() => navigate('/login')} className="btn-primary">
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B6B93]/80 to-[#4BA3C7]/60" />
        <Card className="w-full max-w-md glass border-white/20 shadow-2xl relative z-10">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Password Reimpostata!</h2>
            <p className="text-slate-600 mb-6">
              La tua password è stata aggiornata con successo. Ora puoi accedere con la nuova password.
            </p>
            <Button onClick={() => navigate('/login')} className="btn-primary">
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1B6B93]/80 to-[#4BA3C7]/60" />
      
      <Card className="w-full max-w-md glass border-white/20 shadow-2xl animate-fade-in relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#1B6B93]/10 flex items-center justify-center">
            <KeyRound className="w-8 h-8 text-[#1B6B93]" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-[#1B6B93]">
              Nuova Password
            </CardTitle>
            <CardDescription className="text-slate-600 mt-2">
              Inserisci la nuova password per <strong>{email}</strong>
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nuova Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="input-field"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full btn-primary mt-6"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <KeyRound className="w-5 h-5 mr-2" />
              )}
              Reimposta Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
